<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useCsvStore } from '../../../stores';
import { closeModal, updateShow } from '../../../app/components/modal/modals';
import type { ModalProps } from '../../../app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DATA_QA } from './delete-csv.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';

const log = logger.getChild('DeleteCsv');
log.debug('Mounting');

const props = defineProps<ModalProps>();

if (!props.meta?.id) {
  void closeModal();
  throw new Error('Missing csv ID');
}

const csvStore = useCsvStore();

const csv = csvStore.watch(() => props.meta?.id ?? '', 'DeleteCsv');
const mounted = ref(false);

watch(
  () => csv.value.error,
  (error) => {
    if (error) {
      log.log('Error fetching csv', error);
      void closeModal();
      // show toast
    }
  },
);

watch(
  [() => csv.value.item, () => csv.value.loading],
  ([csvToDelete, loading]) => {
    if (!loading && !csvToDelete) {
      log.log('CSV was deleted');
      void closeModal();
      // show toast
    }
  },
);

const confirm = async () => {
  log.debug('Confirm');
  if (!csv.value.item?.csvId) {
    log.error('csv ID is missing');
    return;
  }

  void closeModal();
  log.debug('Discarding csv', csv.value.item.id);
  csvStore.discard(csv.value.item.id, {
    onSuccess: () => {
      // show toast
    },
    onRetry: () => {
      // show toast
    },
    onError: (error) => {
      log.log('Error discarding csv', error);
      // show toast
    },
  });
};

const cancel = () => {
  log.debug('Cancel');
  void closeModal();
};

onMounted(() => {
  log.debug('Mounted');
  mounted.value = true;
});
const show = computed(() => mounted.value && !!csv.value.item);
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('CSV_DELETE_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    {{ $t('CSV_DELETE_DESCRIPTION', { name: csv.item?.name }) }}
    <footer class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CANCEL" importance="clear" @click="cancel">
        {{ $t('CSV_DELETE_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        kind="danger"
        importance="primary"
        class="d-ml6"
        @click="confirm"
      >
        {{ $t('CSV_DELETE_CONFIRM') }}
      </DtButton>
    </footer>
  </DtModal>
</template>
